import { Add, KeyboardBackspace } from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetPatientInvoicesQuery } from "src/api/services/paymentsApi";
import Loading from "src/components/Loading/Loading";
import { useIsMobile } from "src/hooks/useIsMobile";
import PayInvoiceModal from "./components/PayInvoiceModal";
import AddNewPayment from "../PaymentMethods/components/AddNewPayment";
import { useLatestOrder } from "src/context/LatestOrder";
import FormattedInvoice from "./components/FormattedInvoice";
import StripeContextProvider from "src/context/StripeContext";

const invoiceStatuses = [
  {
    value: "paid",
    label: "Paid",
  },
  { value: "open", label: "Open" },
  { value: "void", label: "Void" },
];

const TransactionHistory = () => {
  const latestOrder = useLatestOrder((o) => o);

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "paid";
  const {
    data: invoices,
    isLoading,
    refetch,
  } = useGetPatientInvoicesQuery({
    status,
  });
  const page = "account.transaction-history";
  const { t } = useTranslation();
  const [openModal, setOpen] = useState<boolean>(false);
  const [openNewMethod, setOpenNewMethod] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);

  const statuses = useMemo(() => {
    if (latestOrder?.product.billing_model === "one_time") {
      return invoiceStatuses.filter((s) => s.value != "open");
    }

    return invoiceStatuses;
  }, [latestOrder?.product.billing_model]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    searchParams.set("status", newValue);
    setSearchParams(searchParams);
  };

  const handleOpenModal = useCallback((invoiceId: string) => {
    setOpen((o) => !o);
    setSelectedInvoice(invoiceId);
  }, []);

  const handleOpenNewMethod = useCallback(() => {
    setOpenNewMethod((o) => !o);
  }, []);

  return (
    <>
      <Container maxWidth="xl" disableGutters>
        <Stack gap={isMobile ? 2 : 4}>
          <Stack direction="row" alignItems="center">
            {isMobile ? (
              <IconButton
                sx={{ color: "#00B6B8" }}
                onClick={() => navigate(-1)}
              >
                <KeyboardBackspace fontSize="large" />
              </IconButton>
            ) : null}
            <Stack direction="row" gap={2}>
              <Typography variant="h5" fontWeight={700}>
                {t(`${page}.header`)}
              </Typography>
              <Button
                onClick={handleOpenNewMethod}
                startIcon={<Add />}
                variant="text"
              >
                {t("payment-methods.add")}
              </Button>
            </Stack>
          </Stack>
          <Box sx={{ width: "100%", borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={status}
              onChange={handleChange}
              textColor="secondary"
              indicatorColor="secondary"
              aria-label="secondary tabs example"
            >
              {statuses.map((s) => (
                <Tab key={s.value} value={s.value} label={s.label} />
              ))}
            </Tabs>
          </Box>
          {isLoading ? (
            <Loading />
          ) : invoices?.length === 0 ? (
            <Typography>{t(`${page}.message`)}</Typography>
          ) : (
            invoices?.map((invoice) => (
              <FormattedInvoice
                invoice={invoice}
                key={invoice.id}
                page={page}
                handleOpenModal={handleOpenModal}
              />
            ))
          )}
        </Stack>
      </Container>
      <PayInvoiceModal
        open={openModal}
        invoiceId={selectedInvoice}
        onClose={setOpen}
        refetchInvoices={refetch}
      />
      <StripeContextProvider>
        <AddNewPayment open={openNewMethod} onClose={handleOpenNewMethod} />
      </StripeContextProvider>
    </>
  );
};

export default TransactionHistory;
